import { Controller } from "@hotwired/stimulus";

export default class PanelSettingsController extends Controller {
  static values = { panelId: String, settings: Object };

  connect() {
    this._apply(this.settingsValue);
    this._startObserver();
  }

  disconnect() {
    this._observer?.disconnect();
    clearTimeout(this._timeout);
  }

  settingsValueChanged(settings) {
    this._apply(settings);
  }

  saveBoolean(event) {
    this._save(event.target.dataset.key, event.target.checked);
  }

  saveString(event) {
    this._save(event.target.dataset.key, event.target.value);
  }

  saveNumber(event) {
    this._save(event.target.dataset.key, parseInt(event.target.value, 10) || 0);
  }

  saveBotList(event) {
    const bots = event.target.value
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);
    this._save("bot_list", bots);
  }

  saveHighlightWords(event) {
    const words = event.target.value
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);
    this._save("highlight_words", words);
  }

  saveMultiCheckbox(event) {
    const key = event.target.dataset.key;
    const value = event.target.dataset.value;
    const current = [...(this.settingsValue[key] || [])];
    if (event.target.checked) {
      if (!current.includes(value)) current.push(value);
    } else {
      const idx = current.indexOf(value);
      if (idx > -1) current.splice(idx, 1);
    }
    this._save(key, current);
  }

  savePlatformFilter(event) {
    const platform = event.target.dataset.value;
    const current = [...(this.settingsValue.platforms || [])];
    if (event.target.checked) {
      if (!current.includes(platform)) current.push(platform);
    } else {
      const idx = current.indexOf(platform);
      if (idx > -1) current.splice(idx, 1);
    }
    this._save("platforms", current);
  }

  _save(key, value) {
    const updated = { ...this.settingsValue, [key]: value };
    this.settingsValue = updated;
    clearTimeout(this._timeout);
    this._timeout = setTimeout(() => {
      fetch("/api/v1/user_preferences/panel_settings", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ panel_id: this.panelIdValue, settings: { [key]: value } }),
      });
    }, 400);
  }

  _startObserver() {
    const panelId = this.panelIdValue;
    if (panelId !== "live-chat") return;
    const body = this.element.querySelector(".sc-panel-body");
    if (!body) return;
    this._observer = new MutationObserver(() => this._applyChat(this.settingsValue));
    this._observer.observe(body, { childList: true, subtree: true });
  }

  _apply(settings) {
    switch (this.panelIdValue) {
      case "live-chat":
        this._applyChat(settings);
        break;
      case "mod-actions":
        this._applyModActions(settings);
        break;
      case "obs-status":
        this._applyObs(settings);
        break;
      case "live-events":
        this._applyEvents(settings);
        break;
    }
  }

  _applyChat(s) {
    const body = this.element.querySelector(".sc-panel-body");
    if (!body) return;

    const platforms = s.platforms || [];
    const bots = (s.hide_bots ? s.bot_list || [] : []).map((b) => b.toLowerCase());
    const words = (s.highlight_mentions ? s.highlight_words || [] : []).map((w) => w.toLowerCase());

    body.querySelectorAll(".sc-chat-msg").forEach((msg) => {
      const platform = msg.dataset.platform || "";
      const username = (msg.dataset.username || "").toLowerCase();
      const text = (msg.querySelector(".sc-chat-text")?.textContent || "").toLowerCase();

      const hiddenByPlatform = platforms.length > 0 && !platforms.includes(platform);
      const hiddenByBot = bots.length > 0 && bots.includes(username);
      msg.hidden = hiddenByPlatform || hiddenByBot;

      msg.classList.toggle(
        "sc-chat-msg--highlight",
        words.some((w) => text.includes(w)),
      );
    });

    body.querySelectorAll(".sc-chat-timestamp").forEach((el) => {
      el.classList.toggle("hidden", !s.show_timestamps);
    });

    body.querySelectorAll(".sc-chat-platform").forEach((el) => {
      el.hidden = !s.show_platform_badges;
    });

    body.querySelectorAll("[data-role-icon]").forEach((el) => {
      el.hidden = !s.show_role_icons;
    });

    // Auto-prune: cap count
    const pruneCount = parseInt(s.prune_count || 200, 10);
    if (pruneCount > 0) {
      const msgs = [...body.querySelectorAll(".sc-chat-msg")];
      if (msgs.length > pruneCount) {
        msgs.slice(0, msgs.length - pruneCount).forEach((el) => el.remove());
      }
    }
  }

  _applyModActions(s) {
    const showTypes = s.show_types || [];
    this.element.querySelectorAll("[data-action-type]").forEach((row) => {
      row.hidden = showTypes.length > 0 && !showTypes.includes(row.dataset.actionType);
    });
  }

  _applyObs(s) {
    const map = {
      show_stream_controls: "obs-stream-controls-wrap",
      show_recording_state: "obs-record-status-wrap",
      show_current_scene: "obs-current-scene-wrap",
    };
    Object.entries(map).forEach(([key, id]) => {
      const el = document.getElementById(id);
      if (el) el.hidden = s[key] === false;
    });
  }

  _applyEvents(s) {
    const showTypes = s.show_types || [];
    const minBits = parseInt(s.min_bits || 0, 10);
    this.element.querySelectorAll("[data-event-type]").forEach((row) => {
      const type = row.dataset.eventType;
      const bits = parseInt(row.dataset.bitsAmount || 0, 10);
      row.hidden =
        (showTypes.length > 0 && !showTypes.includes(type)) || (type === "cheer" && bits < minBits);
    });
  }
}
